import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCrypto } from "../context/CryptoContext";
import { SecureMedia } from "./Media";
import { encryptAndUpload, validateVideoFile, MAX_SLIDES } from "../lib/mediaUpload";

// Decrypt an existing Hearth post/comment body for editing.
const useDecText = (item) => {
  const { decryptNetText } = useCrypto();
  const [txt, setTxt] = useState(null);
  useEffect(() => {
    if (item.enc && item.ct) decryptNetText(item.ct, item.iv).then((t) => setTxt(t || "")).catch(() => setTxt(""));
    else setTxt(item.text || "");
  }, [item.ct, item.iv, item.enc, item.text, decryptNetText]);
  return txt;
};

// Re-encrypt in the item's original mode (network key vs plain).
const useEncodeBody = () => {
  const { encryptNetText } = useCrypto();
  return async (item, text) => {
    if (!item.enc) return { text };
    if (!text) return { ct: null, iv: null };
    const e = await encryptNetText(text);
    if (!e?.enc) throw new Error("Unlock your vault to edit encrypted posts");
    return { ct: e.ct, iv: e.iv };
  };
};

const Buttons = ({ id, saving, disabled, onSave, onCancel }) => (
  <div className="flex gap-2 mt-2">
    <button onClick={onSave} disabled={saving || disabled} data-testid={`hearth-edit-save-${id}`} className="flex items-center gap-1 bg-[#14A89A] text-[#0A0A0A] font-semibold px-3 py-1.5 font-body text-xs disabled:opacity-40">{saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save</button>
    <button onClick={onCancel} data-testid={`hearth-edit-cancel-${id}`} className="px-3 py-1.5 font-body text-xs text-zinc-400 hover:text-white border border-white/15">Cancel</button>
  </div>
);

export const HearthPostEditor = ({ hearthId, post, onDone }) => {
  const { encryptNetBytes, hasNetwork } = useCrypto();
  const encode = useEncodeBody();
  const original = useDecText(post);
  const [draft, setDraft] = useState(null);
  const [media, setMedia] = useState(post.media || []);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState("");
  const fileRef = useRef();
  useEffect(() => { if (original !== null && draft === null) setDraft(original); }, [original, draft]);
  const room = MAX_SLIDES - media.length - files.length;
  const addFiles = async (list) => {
    const incoming = Array.from(list || []).slice(0, Math.max(0, room));
    const ok = [];
    for (const f of incoming) {
      if (f.type.startsWith("video/")) { const err = await validateVideoFile(f); if (err) { toast.error(err); continue; } }
      ok.push(f);
    }
    if ((list?.length || 0) > room) toast.error(`Only ${MAX_SLIDES} items per post.`);
    setFiles((p) => [...p, ...ok]);
  };
  const empty = !(draft || "").trim() && media.length + files.length === 0;
  const save = async () => {
    try {
      const added = [];
      for (let i = 0; i < files.length; i++) {
        setSaving(`Encrypting & uploading ${i + 1}/${files.length}…`);
        added.push(await encryptAndUpload(files[i], { encryptNetBytes, hasNetwork, onPct: () => {} }));
      }
      setSaving("Saving…");
      const body = { ...(await encode(post, (draft || "").trim())), media: [...media, ...added] };
      await api.patch(`/hearths/${hearthId}/posts/${post.id}`, body);
      onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Couldn't save"); setSaving(""); }
  };
  if (draft === null) return <span className="text-zinc-600 text-sm">·····</span>;
  return (
    <div data-testid={`hearth-edit-box-${post.id}`}>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} autoFocus data-testid={`hearth-edit-input-${post.id}`}
        className="w-full bg-[#0A0A0A] border border-[#14A89A]/50 px-3 py-2 text-white font-body text-sm focus:outline-none resize-y" />
      {(media.length > 0 || files.length > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2" data-testid={`hearth-edit-media-${post.id}`}>
          {media.map((m, i) => (
            <div key={m.path} className="relative aspect-square overflow-hidden bg-black" data-testid={`hearth-edit-media-item-${i}`}>
              <SecureMedia path={m.path} iv={m.iv} enc={post.enc} type={m.type} thumb={m.thumb} thumbIv={m.thumb_iv} className="w-full h-full object-cover" />
              <button type="button" onClick={() => setMedia((xs) => xs.filter((x) => x.path !== m.path))} aria-label="Remove" data-testid={`hearth-edit-media-remove-${i}`} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-500"><X size={12} /></button>
            </div>
          ))}
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="relative aspect-square bg-white/5 flex items-center justify-center p-2 text-center" data-testid={`hearth-edit-new-${i}`}>
              <span className="font-body text-[10px] text-zinc-300 break-all">{f.name}</span>
              <button type="button" onClick={() => setFiles((xs) => xs.filter((_, j) => j !== i))} aria-label="Remove" className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-500"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Buttons id={post.id} saving={!!saving} disabled={empty} onSave={save} onCancel={() => onDone(false)} />
        {room > 0 && <button type="button" onClick={() => fileRef.current?.click()} data-testid={`hearth-edit-add-media-${post.id}`} className="flex items-center gap-1.5 mt-2 text-zinc-400 hover:text-white font-body text-xs"><ImagePlus size={14} /> Add photo / video</button>}
      </div>
      {saving && <p className="font-body text-[11px] text-zinc-500 mt-1">{saving}</p>}
    </div>
  );
};

export const HearthCommentEditor = ({ hearthId, postId, comment, onDone }) => {
  const encode = useEncodeBody();
  const original = useDecText(comment);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (original !== null && draft === null) setDraft(original); }, [original, draft]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/hearths/${hearthId}/posts/${postId}/comments/${comment.id}`, await encode(comment, draft.trim()));
      onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Couldn't save"); setSaving(false); }
  };
  if (draft === null) return <span className="text-zinc-600 text-xs">·····</span>;
  return (
    <div className="w-full" data-testid={`hearth-comment-edit-box-${comment.id}`}>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} autoFocus data-testid={`hearth-comment-edit-input-${comment.id}`}
        className="w-full min-w-[220px] bg-[#0A0A0A] border border-[#14A89A]/50 px-2 py-1.5 text-white font-body text-xs focus:outline-none resize-y" />
      <Buttons id={`c-${comment.id}`} saving={saving} disabled={!draft.trim()} onSave={save} onCancel={() => onDone(false)} />
    </div>
  );
};
